/**
 * A tiny pub/sub for "the server told us we are no longer authenticated".
 *
 * Why this exists: the app has no shared axios instance (41 files call the global `axios`
 * directly) and 28 more use raw `fetch`, so there is no single place a 401 can be handled.
 * Rather than migrate every call site, HTTP handlers installed once at boot
 * (see `installHttpAuthHandlers`) funnel every 401 into this module, and `AuthProvider`
 * subscribes to it.
 *
 * Deliberately framework-free so it can be imported from non-React modules.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * Latch. A single page can fire a dozen concurrent requests that all 401 at once; without
 * this, every one of them would notify and each listener would re-run. The latch is cleared
 * by `markSessionActive()` whenever we next see a successful authentication.
 */
let alreadyExpired = false;

/** Subscribe to session-expiry notifications. Returns an unsubscribe function. */
export function onSessionExpired(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Report that an authenticated request came back 401. Safe to call repeatedly and from
 * concurrent requests — only the first call after the most recent `markSessionActive()`
 * notifies listeners.
 */
export function notifySessionExpired(): void {
  if (alreadyExpired) return;
  alreadyExpired = true;

  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      // A throwing listener must not prevent the others from running, and must not
      // propagate into whichever HTTP response happened to trip the latch.
      console.error('[auth] session-expiry listener threw:', err);
    }
  });
}

/** Clear the latch. Call after a successful login or auth check. */
export function markSessionActive(): void {
  alreadyExpired = false;
}

/** Test seam: drop all listeners and reset the latch. */
export function resetSessionExpiryForTests(): void {
  listeners.clear();
  alreadyExpired = false;
}
